import React, { useState, useEffect } from "react";
import { Dialog, Switch, Button, Avatar } from "@/components/ui";
import { extractInitials, useSocialProfiles } from "@/utils";
import { SocialIdentity } from "@/services/bedrock";
import { SocialNetworkProfileServices } from "@/services/bedrock/SocialNetworkProfileServices";
import { toast } from "sonner";
import { RefreshCw, FileText, Star } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  socialIdentity: SocialIdentity;
  userId: string | number;
}

export function SocialProfileReviewPrefsDialog({ open, onOpenChange, socialIdentity, userId }: Props) {
  const { refetchProfile } = useSocialProfiles(userId);
  const [syncReviews, setSyncReviews] = useState(false);
  const [acceptNewReviews, setAcceptNewReviews] = useState(false);
  const [preferred, setPreferred] = useState(false);
  const [isSavingReviewSettings, setIsSavingReviewSettings] = useState(false);

  useEffect(() => {
    if (socialIdentity) {
      setSyncReviews(!!socialIdentity.syncReviews);
      setAcceptNewReviews(!!socialIdentity.acceptNewReviews);
      setPreferred(!!socialIdentity.preferred);
    }
  }, [socialIdentity]);

  const handleSaveReviewSettings = async () => {
    const socialProfileId = socialIdentity.socialNetworkProfileId;
    if (!socialProfileId) return;
    setIsSavingReviewSettings(true);
    try {
      await SocialNetworkProfileServices.updateReviewSettings(userId, socialProfileId, {
        syncReviews,
        acceptNewReviews,
        preferred,
      });
      toast.success("Review settings updated successfully");
      await refetchProfile(socialProfileId);
      onOpenChange(false);
    } catch (err: any) {
      console.error("Failed to update review settings", err);
      toast.error(err?.message || "Failed to update review settings. Please try again.");
    } finally {
      setIsSavingReviewSettings(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} showCloseButton size="md">
      <Dialog.Header>
        <Dialog.Title>Review Preferences</Dialog.Title>
        <Dialog.Description>
          Manage how reviews and review requests are handled for this social profile.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="flex flex-col gap-6">
        {/* Social Identity Info */}
        <div className="flex items-center gap-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
          <Avatar size="lg" className="ring-1 ring-slate-200 shadow-sm">
            <Avatar.Image src={socialIdentity.profileImageUrl} alt="Profile" />
            <Avatar.Fallback>{extractInitials(socialIdentity.displayName)}</Avatar.Fallback>
          </Avatar>

          <div className="flex flex-col leading-tight overflow-hidden">
            <span className="font-semibold text-base text-slate-900 truncate">{socialIdentity.displayName}</span>
            <span className="text-sm text-slate-500 truncate mt-0.5">
              {socialIdentity.socialNetworkId === "GL"
                ? "Google Maps Profile"
                : socialIdentity.socialNetworkId === "ZL"
                ? "Zillow Profile"
                : "Facebook Page"}
            </span>
          </div>
        </div>

        {/* Toggles Container */}
        <div className="flex flex-col gap-5">
          {/* Sync Reviews */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <RefreshCw className="size-4 text-slate-500 mt-1 shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">Sync Reviews</span>
                <span className="text-xs text-slate-500 mt-0.5 leading-normal">
                  Automatically import and display reviews from this platform on your profile.
                </span>
              </div>
            </div>
            <Switch checked={syncReviews} onCheckedChange={setSyncReviews} />
          </div>

          {/* Accept New Reviews */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <FileText className="size-4 text-slate-500 mt-1 shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">Display in Feedback & Review Forms</span>
                <span className="text-xs text-slate-500 mt-0.5 leading-normal">
                  Include this destination as an option on your feedback and review forms.
                </span>
              </div>
            </div>
            <Switch checked={acceptNewReviews} onCheckedChange={setAcceptNewReviews} />
          </div>

          {/* Preferred */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Star className="size-4 text-slate-500 mt-1 shrink-0" />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-slate-900">Preferred Platform</span>
                <span className="text-xs text-slate-500 mt-0.5 leading-normal">
                  Prioritize this platform as the primary choice for your feedback and review forms.
                </span>
              </div>
            </div>
            <Switch checked={preferred} onCheckedChange={setPreferred} />
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer showCloseButton>
        <Button onClick={handleSaveReviewSettings} loading={isSavingReviewSettings}>
          Save Changes
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
